import { useEffect, useState } from 'react'
import { PreviewConfirmModal } from 'components/dialog/ModalTemplate'
import CurrencyInput from 'components/form/CurrencyInput'
import {
  integerValue,
  invalidNumberInput,
  isEmptyObject,
  isNullOrUndefined,
} from 'utils'
import useValidator from 'utils/hooks/useValidator'
import classNames from 'classnames'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { fail, failWithErrorResponse, success } from 'features/toast/Toast'
import {
  hideAdGroupContentMatchingMaxBiddingAmountEdit,
  hidePreview,
  setCustomContentMatchingMaxBiddingAmountYn,
  showPreview,
  updateAdGroupContentMatchingMaxBiddingAmountEdit,
} from 'features/dashboard/operation/adGroupContentMatchingMaxBiddingAmountEdit/adGroupContentMatchingMaxBiddingAmountEditSlice'
import { Modal } from 'components/dialog/Modals'
import Table from 'components/table/Table'
import { refreshDashboard } from 'features/dashboard/dashboardCommonSlice'
import Loading from 'components/loading/Loading'
import CheckBox from 'components/form/CheckBox'
import FixedTable from 'components/table/FixedTable'

const AdGroupContentMatchingMaxBiddingAmountEditModal = () => {
  const dispatch = useDispatch()
  const {
    visible,
    isSubmitting,
    previewVisible,
    isLoading,
    adGroups,
    loadingError,
    form: {
      contentMatchingMaxBiddingAmount,
      customContentMatchingMaxBiddingAmountYn,
    },
    preview: {
      contentMatchingMaxBiddingAmount: previewContentMatchingMaxBiddingAmount,
      customContentMatchingMaxBiddingAmountYn:
        previewCustomContentMatchingMaxBiddingAmountYn,
    },
  } = useSelector(
    ({ dashboard }) =>
      dashboard.operation.adGroupContentMatchingMaxBiddingAmountEdit,
    shallowEqual
  )
  const [
    contentMatchingMaxBiddingAmountInput,
    setContentMatchingMaxBiddingAmountInput,
  ] = useState(contentMatchingMaxBiddingAmount)
  useEffect(() => {
    if (visible) {
      setContentMatchingMaxBiddingAmountInput(contentMatchingMaxBiddingAmount)
    }
  }, [visible, contentMatchingMaxBiddingAmount])
  const { errors, validate } = useValidator(
    () => ({
      contentMatchingMaxBiddingAmount:
        customContentMatchingMaxBiddingAmountYn === 'Y' &&
        (contentMatchingMaxBiddingAmountInput === '' ||
          invalidNumberInput(
            contentMatchingMaxBiddingAmountInput,
            100000,
            70,
            10
          )) &&
        true,
    }),
    [
      contentMatchingMaxBiddingAmountInput,
      customContentMatchingMaxBiddingAmountYn,
    ]
  )

  const handlePreview = async () => {
    try {
      await validate()
      if (isEmptyObject(errors)) {
        dispatch(showPreview(contentMatchingMaxBiddingAmountInput))
      }
    } catch (ignore) {}
  }
  const handleConfirm = async ({ preventClose }) => {
    try {
      await validate()
      if (isEmptyObject(errors)) {
        const { error } = await dispatch(
          updateAdGroupContentMatchingMaxBiddingAmountEdit()
        )
        if (error) {
          preventClose()
          fail(
            '광고그룹 콘텐츠 매체 입찰가 변경사항을 적용할 수 없습니다.\n다시 시도해 주세요.'
          )
        } else {
          dispatch(refreshDashboard())
          success('광고그룹 콘텐츠 매체 입찰가 변경사항이 적용되었습니다.')
        }
      }
    } catch (error) {
      preventClose()
    }
  }

  useEffect(
    () => () => {
      dispatch(hideAdGroupContentMatchingMaxBiddingAmountEdit())
    },
    [dispatch]
  )
  useEffect(() => {
    if (!isEmptyObject(errors)) {
      dispatch(hidePreview())
    }
  }, [dispatch, errors])
  useEffect(() => {
    if (loadingError) {
      dispatch(hideAdGroupContentMatchingMaxBiddingAmountEdit())
      failWithErrorResponse(loadingError, {
        messageMap: {
          104007: '삭제된 값이 존재합니다.\n새로고침 후 다시 시도해 주세요.',
        },
      })
    }
  }, [dispatch, loadingError])

  const warningCount = adGroups.filter(
    ({
      biddingStrategy: {
        maxBiddingAmount,
        customContentMatchingMaxBiddingAmountYn: yn,
        contentMatchingMaxBiddingAmount: amount,
      },
    }) => {
      const before = yn === 'Y' ? amount : maxBiddingAmount
      const after =
        previewCustomContentMatchingMaxBiddingAmountYn === 'Y'
          ? previewContentMatchingMaxBiddingAmount
          : maxBiddingAmount
      const percentage =
        isNullOrUndefined(before) || isNullOrUndefined(after)
          ? 0
          : (after / before) * 100
      return percentage >= 500
    }
  ).length
  const preview = previewVisible && isEmptyObject(errors)
  const changed =
    contentMatchingMaxBiddingAmountInput !==
      previewContentMatchingMaxBiddingAmount ||
    customContentMatchingMaxBiddingAmountYn !==
      previewCustomContentMatchingMaxBiddingAmountYn

  return (
    <Modal on={visible} className="adbudget_layer">
      <PreviewConfirmModal
        title="광고그룹 콘텐츠 매체 입찰가 변경"
        isSubmitting={isSubmitting}
        onPreview={handlePreview}
        onConfirm={handleConfirm}
        previewDisabled={!changed && preview}
        confirmDisabled={changed || !preview}
        handleClose={() =>
          dispatch(hideAdGroupContentMatchingMaxBiddingAmountEdit())
        }>
        <p className="desc_copy">
          선택된 그룹의 콘텐츠 매체 입찰가가 설정된 금액으로 일괄 변경됩니다.
          콘텐츠 매체 입찰가를 설정하지 않을 경우 콘텐츠 매체 광고는 기본
          입찰가를 기준으로 입찰 합니다.
        </p>
        <div className="group_budget">
          {isLoading ? (
            <Loading />
          ) : (
            <>
              <strong className="tit_budget">콘텐츠 매체 입찰가</strong>
              <div className="group_item">
                <CurrencyInput
                  min={70}
                  max={100000}
                  step={10}
                  maxLength={6}
                  disabled={customContentMatchingMaxBiddingAmountYn === 'N'}
                  value={contentMatchingMaxBiddingAmountInput}
                  error={errors.contentMatchingMaxBiddingAmount}
                  onValueChange={value =>
                    setContentMatchingMaxBiddingAmountInput(integerValue(value))
                  }
                />
                <CheckBox
                  label="미설정"
                  checked={customContentMatchingMaxBiddingAmountYn === 'N'}
                  onCheckedChange={checked =>
                    dispatch(
                      setCustomContentMatchingMaxBiddingAmountYn(
                        checked ? 'N' : 'Y'
                      )
                    )
                  }
                />
              </div>
              <p
                className={classNames({
                  desc_item: isNullOrUndefined(
                    errors.contentMatchingMaxBiddingAmount
                  ),
                  txt_error: errors.contentMatchingMaxBiddingAmount,
                })}>
                광고그룹 콘텐츠 매체 입찰가는 최소 70원 ~ 최대 100,000원까지
                10원 단위로 설정할 수 있습니다.
              </p>
              {preview && (
                <>
                  <FixedTable caption="콘텐츠 매체 입찰가 변경 이력 표">
                    <Table.Head>
                      <Table.Column
                        title="선택 광고그룹"
                        className="inner_cell"
                        width={194}
                      />
                      <Table.Column
                        title="변경 전"
                        className="inner_cell"
                        width={171}
                      />
                      <Table.Column title="변경 후" className="inner_cell" />
                    </Table.Head>
                    <Table.Body>
                      {adGroups.map(
                        ({
                          id,
                          name,
                          biddingStrategy: {
                            maxBiddingAmount,
                            customContentMatchingMaxBiddingAmountYn: yn,
                            contentMatchingMaxBiddingAmount: amount,
                          },
                        }) => {
                          const before = yn === 'Y' ? amount : maxBiddingAmount
                          const after =
                            previewCustomContentMatchingMaxBiddingAmountYn ===
                            'Y'
                              ? previewContentMatchingMaxBiddingAmount
                              : maxBiddingAmount
                          const percentage =
                            isNullOrUndefined(before) ||
                            isNullOrUndefined(after)
                              ? 0
                              : (after / before) * 100
                          const fixed = Math.ceil(percentage.toFixed(1))
                          return (
                            <Table.Row
                              rowId={id}
                              key={id}
                              className={classNames({
                                update: before !== after,
                              })}>
                              <td>
                                <div className="inner_cell">
                                  <span className="txt_limit2">{name}</span>
                                </div>
                              </td>
                              <td>
                                <div className="inner_cell">
                                  {yn === 'N' && '[미설정] '}
                                  {before.toLocaleString()}원
                                </div>
                              </td>
                              <td>
                                <div className="inner_cell">
                                  {previewCustomContentMatchingMaxBiddingAmountYn ===
                                    'N' && '[미설정] '}
                                  {after.toLocaleString()}원
                                  {percentage >= 500 && (
                                    <span className="txt_error">
                                      {fixed.toLocaleString()}%로 증가폭이
                                      큽니다.
                                    </span>
                                  )}
                                </div>
                              </td>
                            </Table.Row>
                          )
                        }
                      )}
                    </Table.Body>
                  </FixedTable>
                  {warningCount > 0 && (
                    <p className="txt_error">
                      변경 전 금액 대비 증가폭이 큰 입력값이 {warningCount}개
                      존재합니다. 입력값이 맞는지 확인하세요.
                    </p>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </PreviewConfirmModal>
    </Modal>
  )
}
export default AdGroupContentMatchingMaxBiddingAmountEditModal
